import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { isDate } from 'date-fns';
import {
  Formik,
  Form,
  Field,
} from 'formik';
import {
  Autocomplete,
  CheckboxWithLabel,
  TextField,
  Select,
  Switch,
} from 'formik-mui';
import parse from 'autosuggest-highlight/parse';
import match from 'autosuggest-highlight/match';
import {
  Badge,
  Box,
  Button,
  ClickAwayListener,
  Collapse,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  Select as MUISelect,
  Switch as MUISwitch,
  TextField as MUITextField,
  Tooltip,
  InputLabel,
  Typography,
} from '@mui/material';
import {
  Add,
  Search,
  Remove,
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import FormikDatePicker from 'generic/components/forms/FormikDatePicker';
import AutocompleteAjaxSearch from 'generic/components/forms/AutocompleteAjaxSearch';
import { isHeaderBackgroundColorDark } from 'generic/utils/colorUtils';
import { baseProptype } from 'generic/core/qes/proptypes';
import QES_CONSTANTS from 'generic/core/qes/constants';

const { DATE_INTERVAL_COMPARATOR } = QES_CONSTANTS;
const betweenDatesCode = DATE_INTERVAL_COMPARATOR.between.toString();

const autocompleteRenderOption = (props, option, { inputValue }) => {
  const matches = match(option.libelle, inputValue, { insideWords: true });
  const parts = parse(option.libelle, matches);

  const highlightStyle = {
    fontWeight: 'bold',
  };

  return (
    <li {...props}>
      <div>
        {parts.map((part, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <span key={index} style={part.highlight ? highlightStyle : {}}>
            {part.text}
          </span>
        ))}
      </div>
    </li>
  );
};

/** @type {import('@mui/material').SxProps<import('@mui/material').Theme>} */
const rootSx = {
  position: 'relative',
  width: '100%',
};

const FormSearchAdvanced = ({
  activeBaseId,
  activeBaseLabel,
  bases,
  handleChangeActiveBase = null,
  onSubmit = null,
  loading,
  initialValues = {},
  handleClear = null,
  mainFieldCode = 'F_2',
  fieldsAdvanced = [],
  isVisible = true,
  fullPage = false,
  formDisabled = false,
}) => {
  const [isFormVisible, setIsFormVisible] = useState(fullPage);
  const { t } = useTranslation();
  const mainInput = useRef();

  useEffect(() => {
    if (isVisible && mainInput.current) {
      mainInput.current.focus();
    }
  }, [isVisible]);

  const renderField = (field, form) => {
    switch (field.type) {
      case 'text':
        return (
          <Field
            component={TextField}
            label={field.libelle}
            name={field.code}
            fullWidth
            disabled={formDisabled}
          />
        );
      case 'liste':
        return (
          <Field
            label={field.libelle}
            component={Select}
            name={field.code}
            fullWidth
            displayEmpty
            MenuProps={{
              disablePortal: true,
            }}
          >
            {_.map(
              field.values,
              (value) => (
                <MenuItem
                  key={value.code}
                  value={value.code}
                >
                  {value.code && value.libelle ? (
                    value.libelle
                  ) : (
                    <Typography color="text.neutral" sx={{ fontStyle: 'italic' }}>
                      {t('form.unspecified')}
                    </Typography>
                  )}
                </MenuItem>
              ),
            )}
          </Field>
        );
      case 'liste_multi':
      case 'liste_chosen':
        return (
          <Field
            component={Autocomplete}
            name={field.code}
            options={field.values}
            getOptionLabel={(option) => option?.libelle || ''}
            multiple
            disableCloseOnSelect
            onBlur={() => form.setFieldTouched(field.name)}
            renderInput={(params) => (
              <MUITextField
                {...params}
                label={field.libelle}
              />
            )}
            renderOption={autocompleteRenderOption}
            disabled={formDisabled}
          />
        );
      case 'autocomplete_colonne_direct_regexp':
        return (
          <Field
            component={AutocompleteAjaxSearch}
            activeBaseId={activeBaseId}
            name={field.code}
            freeSolo
            getOptionLabel={(option) => option}
            textFieldProps={{
              label: field.libelle,
            }}
            disabled={formDisabled}
          />
        );
      case 'liste_checkbox_ou':
      case 'liste_checkbox_et_ou':
        return (
          <FormControl
            component="fieldset"
            className="formFieldset"
            fullWidth
          >
            <InputLabel
              sx={(theme) => ({
                mr: 2,
                // eslint-disable-next-line no-nested-ternary
                color: formDisabled
                  ? (theme.palette.mode === 'light' ? '#00000061' : '#FFFFFF61')
                  : 'auto',
              })}
            >
              {field.libelle}
            </InputLabel>
            <FormLabel component="legend">
              {field.type === 'liste_checkbox_et_ou' && (
                <FormControlLabel
                  control={(
                    <Field
                      component={Switch}
                      size="small"
                      type="checkbox"
                      name={`${field.code}_comp`}
                      color="primary"
                      onChange={(e) => {
                        form.setFieldValue(
                          `${field.code}_comp`,
                          e.target.checked ? '1' : '',
                        );
                      }}
                    />
                  )}
                  sx={{ ml: '0' }}
                  label={t('form.checkbox.and')}
                  disabled={formDisabled}
                />
              )}
              <FormControlLabel
                control={(
                  <MUISwitch
                    size="small"
                    type="checkbox"
                    color="primary"
                    onChange={(e) => {
                      form.setFieldValue(
                        field.code,
                        e.target.checked ? _.map(field.values, (item) => item.code) : [],
                      );
                    }}
                    disabled={formDisabled}
                  />
                )}
                sx={{ mr: '0' }}
                label={t('form.checkbox.all')}
              />
            </FormLabel>
            <Box sx={{ ml: 1 }}>
              <FormGroup row>
                {_.map(
                  field.values,
                  (item) => (
                    <Field
                      component={CheckboxWithLabel}
                      name={field.code}
                      key={item.code}
                      value={item.code}
                      Label={{ label: item.libelle }}
                      type="checkbox"
                      color="primary"
                      disabled={formDisabled}
                    />
                  ),
                )}
              </FormGroup>
            </Box>
          </FormControl>
        );
      case 'date_intervalle': {
        const minDate = new Date(1900, 1, 1);
        const maxDate = new Date(2100, 11, 31);
        const fromValue = form.values[`${field.code}_from`];
        const toValue = form.values[`${field.code}_to`];
        return (
          <Grid
            container
            sx={{ display: 'flex' }}
          >
            <Grid
              item
            >
              <Box
                sx={{ mr: '15px' }}
              >
                {form.values[`${field.code}_comp`] !== undefined && (
                  <Field
                    component={Select}
                    name={`${field.code}_comp`}
                    fullWidth
                    MenuProps={{
                      disablePortal: true,
                    }}
                    disabled={formDisabled}
                    onChange={(e) => {
                      // Si la valeur ne correspond pas à "Entre", on vire la valeur
                      // du champ "_to"
                      if (e.target.value !== betweenDatesCode) {
                        form.setFieldValue(
                          `${field.code}_to`,
                          null,
                        );
                      }
                    }}
                  >
                    {_.map(
                      field.comparateurs,
                      (comparator) => (
                        <MenuItem
                          key={comparator.code}
                          value={comparator.code}
                        >
                          {comparator.libelle}
                        </MenuItem>
                      ),
                    )}
                  </Field>
                )}
              </Box>
            </Grid>
            <Grid
              item
            >
              <Box
                sx={{
                  width: 210,
                  mr: 2,
                }}
              >
                <Field
                  component={FormikDatePicker}
                  name={`${field.code}_from`}
                  minDate={minDate}
                  maxDate={isDate(toValue) && _.isEmpty(form.errors[`${field.code}_to`]) ? toValue : maxDate}
                  inputFormat="dd/MM/yyyy"
                  label={`${field.libelle}${form.values[`${field.code}_comp`] === betweenDatesCode ? (
                    ` ${t('form.datepicker.from')}`
                  ) : ''}`}
                  textFieldProps={{
                    inputProps: {
                      placeholder: t('form.datepicker.placeholder'),
                    },
                  }}
                  disabled={formDisabled}
                />
              </Box>
            </Grid>
            {form.values[`${field.code}_comp`] === betweenDatesCode && (
              <Grid
                item
              >
                <Box
                  sx={{ width: 210 }}
                >
                  <Field
                    component={FormikDatePicker}
                    name={`${field.code}_to`}
                    minDate={isDate(fromValue) && _.isEmpty(form.errors[`${field.code}_from`]) ? fromValue : minDate}
                    maxDate={maxDate}
                    inputFormat="dd/MM/yyyy"
                    label={`${field.libelle}${form.values[`${field.code}_comp`] === betweenDatesCode ? (
                      ` ${t('form.datepicker.to')}`
                    ) : ''}`}
                    textFieldProps={{
                      inputProps: {
                        placeholder: t('form.datepicker.placeholder'),
                      },
                    }}
                    disabled={formDisabled}
                  />
                </Box>
              </Grid>
            )}
          </Grid>
        );
      }
      default:
        return '';
    }
  };

  if (_.isEmpty(initialValues)) {
    return null;
  }

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validateOnChange={false}
      onSubmit={(values, { setSubmitting }) => {
        onSubmit(values);
        setSubmitting(false);
        setIsFormVisible(false);
      }}
    >
      {(form) => {
        const keysFormValues = Object.keys(form.values);
        const dateComparatorFieldNameMatcher = /^DATE_(\d+)_comp$/;
        let formValues = form.values;
        keysFormValues.forEach((key) => {
          const matchData = key.match(dateComparatorFieldNameMatcher);
          if (matchData) {
            const x = matchData[1];
            const compKey = `DATE_${x}_comp`;
            const fromKey = `DATE_${x}_from`;
            const toKey = `DATE_${x}_to`;
            if (
              form.values[compKey] === betweenDatesCode
              && form.values[fromKey] !== null
              && form.values[toKey] !== null
            ) {
              formValues = _.omit(formValues, [toKey]);
            }
          }
        });

        const nbParamSearch = _.filter(
          formValues,
          (val, key) => !_.isEmpty(_.toString(val)) && !/^DATE_\d+_comp$/.test(key),
        ).length;

        let searchAdvancedTooltip = t('header.advanced_search');

        if (nbParamSearch > 0) {
          searchAdvancedTooltip = `${searchAdvancedTooltip} ${t('header.number_criteria', { count: nbParamSearch })}`;
        }

        return (
          <ClickAwayListener
            onClickAway={() => (isFormVisible && !fullPage ? setIsFormVisible(false) : _.noop)}
            mouseEvent="onMouseDown"
          >
            <Box sx={[
              rootSx,
              (theme) => !fullPage && {
                transition: 'border-radius 0.3s ease 0.2s',
                borderRadius: '18px',
                backgroundColor: isHeaderBackgroundColorDark(theme)
                  ? alpha(theme.palette.common.white, 0.3)
                  : alpha(theme.palette.common.black, 0.1),
                '&:hover': {
                  backgroundColor: isHeaderBackgroundColorDark(theme)
                    ? alpha(theme.palette.common.white, 0.4)
                    : alpha(theme.palette.common.black, 0.2),
                },
                color: isHeaderBackgroundColorDark(theme)
                  ? theme.palette.common.white
                  : theme.palette.common.black,
              },
              isFormVisible && {
                transition: 'border-radius 0s',
                borderRadius: '18px 18px 0 0',
              },
            ]}
            >
              {!fullPage && (
                <Box sx={{
                  padding: '0 8px',
                  height: '100%',
                  position: 'absolute',
                  pointerEvents: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
                >
                  <Search />
                </Box>
              )}
              <Box
                component={Form}
                sx={rootSx}
              >
                {!fullPage && (
                  <Field
                    margin="none"
                    sx={{ margin: '0' }}
                    variant="standard"
                    component={TextField}
                    fullWidth
                    autoFocus
                    inputRef={mainInput}
                    name={mainFieldCode}
                    label=""
                    placeholder={t('form.omni_placeholder', { activeBaseLabel })}
                    InputProps={{
                      sx: (theme) => ({
                        '&.MuiInput-root': {
                          '&::before, &:hover': {
                            border: 'none',
                          },
                          color: isHeaderBackgroundColorDark(theme)
                            ? theme.palette.common.white
                            : theme.palette.common.black,
                        },
                        '& .MuiInput-input': {
                          padding: '9px 8px 8px 40px',
                          transition: theme.transitions.create('width'),
                        },
                      }),
                      disableUnderline: true,
                      endAdornment: (
                        <InputAdornment position="end">
                          <Tooltip
                            title={searchAdvancedTooltip}
                          >
                            <IconButton
                              sx={(theme) => ({
                                padding: '2px',
                                marginRight: '3px',
                                color: theme.palette.common.black,
                                backgroundColor: theme.palette.common.white,
                                '&:hover': {
                                  backgroundColor: alpha(theme.palette.common.white, 0.8),
                                },
                              })}
                              onClick={() => setIsFormVisible(!isFormVisible)}
                              size="large"
                            >
                              <Badge
                                badgeContent={nbParamSearch}
                                color="error"
                                max={99}
                                size="small"
                                anchorOrigin={{
                                  vertical: 'bottom',
                                  horizontal: 'right',
                                }}
                                sx={{
                                  '& .MuiBadge-badge': {
                                    zIndex: 3,
                                  },
                                }}
                                invisible={nbParamSearch === 0}
                              >
                                {isFormVisible ? <Remove /> : <Add />}
                              </Badge>
                            </IconButton>
                          </Tooltip>
                        </InputAdornment>
                      ),
                    }}
                  />
                )}

                <Box
                  sx={(theme) => !fullPage && ({
                    position: 'absolute',
                    width: '100%',
                    boxShadow: theme.palette.mode === 'light'
                      ? 'rgb(0 0 0) 0px 4px 8px -2px'
                      : 'rgb(80 80 80) 0px 4px 8px -2px',
                    backgroundColor: theme.palette.background.default,
                    color: theme.palette.mode === 'light' ? theme.palette.common.black : theme.palette.common.white,
                    zIndex: '2',
                  })}
                >
                  <Collapse in={isFormVisible}>
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                      <Box sx={{ overflow: 'auto', maxHeight: !fullPage ? '500px' : 'auto', pb: 2 }}>
                        {_.map(
                          fieldsAdvanced,
                          (field) => (
                            field.type && field.code && (
                              <Box key={field.code} sx={{ pl: 2, pr: 2 }}>
                                {renderField(field, form)}
                              </Box>
                            )
                          ),
                        )}
                      </Box>
                      {!formDisabled && (
                        <Box
                          sx={{
                            p: 1.5,
                            pt: 2,
                            borderTop: '1px solid',
                            borderColor: 'divider',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                          }}
                        >
                          <div>
                            {bases && bases.length > 1 && (
                              <MUISelect
                                sx={{ mr: 1 }}
                                value={activeBaseId}
                                onChange={(event) => handleChangeActiveBase(event.target.value)}
                                variant="standard"
                                startAdornment={
                                  <InputAdornment position="start">{t('config.active_base')}</InputAdornment>
                                }
                              >
                                {_.map(bases, (base) => (
                                  <MenuItem key={base.base} value={base.base}>{base.libelle}</MenuItem>
                                ))}
                              </MUISelect>
                            )}
                          </div>
                          <Stack
                            direction="row"
                            spacing={2}
                          >
                            <Button
                              type="reset"
                              variant="text"
                              onClick={handleClear}
                            >
                              {t('form.clear')}
                            </Button>
                            <Button
                              type="submit"
                              disabled={loading || !form.isValid || nbParamSearch === 0}
                            >
                              {t('form.search')}
                            </Button>
                          </Stack>
                        </Box>
                      )}
                    </Box>
                  </Collapse>
                </Box>
              </Box>
            </Box>
          </ClickAwayListener>
        );
      }}
    </Formik>
  );
};

FormSearchAdvanced.propTypes = {
  activeBaseId: PropTypes.number.isRequired,
  activeBaseLabel: PropTypes.string.isRequired,
  bases: PropTypes.arrayOf(
    baseProptype,
  ).isRequired,
  handleChangeActiveBase: PropTypes.func,
  onSubmit: PropTypes.func,
  loading: PropTypes.bool.isRequired,
  handleClear: PropTypes.func,
  initialValues: PropTypes.shape({
    code: PropTypes.string,
    libelle: PropTypes.string,
  }),
  mainFieldCode: PropTypes.string,
  fieldsAdvanced: PropTypes.arrayOf(
    PropTypes.shape({
      code: PropTypes.string,
      libelle: PropTypes.string,
    }),
  ),
  isVisible: PropTypes.bool,
  fullPage: PropTypes.bool,
  formDisabled: PropTypes.bool,
};

export default FormSearchAdvanced;
